// useStudioRecorder.js
// ===========================================================================
// Phase 4D — Recording Studio Mic Toolkit
//
// Single home for the Recording Studio's microphone capture lifecycle. Built
// as a REUSABLE hook so future recording surfaces can adopt it, but consumed
// only by RecordingStudio.jsx / InlineRecorder this pass.
//
// Fixes bundled here (see NEXT_SESSION_PROMPT_PHASE-4D-RECORDING-STUDIO-MIC-
// TOOLKIT.md §4.1):
//   Fix A — capture constraints. We DISABLE Chrome's implicit WebRTC voice-DSP
//           (echoCancellation / autoGainControl / noiseSuppression). On
//           macOS-Chrome with a hot USB condenser (JLab Talk) the AGC clamps
//           the signal to digital silence. This is the load-bearing fix.
//   Fix B — device selection. enumerateDevices() filtered to 'audioinput',
//           explicit deviceId, persisted in localStorage, default = system
//           default.
//   Fix C — silence measurement. We track the LOUDEST short-term RMS window
//           across the take via a Web Audio AnalyserNode and flag the take
//           SILENT if even that loudest window never reaches a tunable floor
//           (SILENCE_RMS_DBFS). Windowed-max (not whole-take average) so a
//           real read with pauses still passes; RMS (not sample-peak) so a
//           stray click can't falsely pass a silent take. Energy-based ONLY —
//           content-agnostic (never blocks on transcript text).

//   Fix D — telemetry. `[StudioRecorder]`-prefixed console logs, gated behind
//           ?debug=1.
//   Fix F — live level. The same AnalyserNode drives a normalized 0..1 level
//           and a band (too-hot / good / quiet / silent) for a live VU meter.
//   Permission state — navigator.permissions.query({ name:'microphone' })
//           exposed to consumers (granted / prompt / denied / unknown).
//
// The hook is intentionally framework-light: it owns refs + timers + the audio
// graph and exposes a flat state object plus imperative start/stop/cancel/reset
// handles. Presentational concerns (buttons, meter bar, dropdown) live in the
// consuming components.
// ===========================================================================

import { useState, useEffect, useRef, useCallback } from 'react';

// ── Tunable constants ──────────────────────────────────────────────────────
export const COUNTDOWN_SECONDS = 3;

// Silence verdict floor — windowed (short-term) RMS. The take is flagged SILENT
// only if its LOUDEST ~46ms window never reaches this level — i.e. no part of
// the recording ever contained real speech energy. This is robust against
//   (a) long pauses inside a genuine read — we take the *max* window, not the
//       average, so a real read with gaps still passes; and
//   (b) stray clicks — a single sample barely moves a 2048-sample window's RMS,
//       so a click can't falsely *pass* an otherwise-silent take (sample-peak
//       could, which is why peak is demoted to telemetry only).
// Calibrate against ambient mic noise: set just above room tone, well below a
// real (even whispered) read. Tunable.
export const SILENCE_RMS_DBFS = -45;

// Legacy peak floor — retained for the meter band + telemetry only. NOT used
// for the silence verdict anymore (see SILENCE_RMS_DBFS above).
export const SILENCE_FLOOR_DBFS = -50;


// Band thresholds (peak dBFS). Drives the live meter color + the VA guidance.
const BAND_QUIET_CEILING_DBFS = -24; // below this (but above silence) = "quiet"
const BAND_GOOD_CEILING_DBFS = -3;   // above this = "too hot" (near clipping)

// localStorage key for the persisted device choice (Fix B).
export const MIC_DEVICE_STORAGE_KEY = 'aai.studio.micDeviceId';

const MIME_TYPE = 'audio/webm';

// ── Helpers ────────────────────────────────────────────────────────────────

// Convert a linear amplitude (0..1) to dBFS. Guards log10(0) = -Infinity.
function amplitudeToDbfs(amp) {
  if (!amp || amp <= 0) return -Infinity;
  return 20 * Math.log10(amp);
}

// Map a peak dBFS reading to a coarse band for the meter / guidance copy.
function dbfsToBand(peakDbfs) {
  if (peakDbfs < SILENCE_FLOOR_DBFS) return 'silent';
  if (peakDbfs < BAND_QUIET_CEILING_DBFS) return 'quiet';
  if (peakDbfs <= BAND_GOOD_CEILING_DBFS) return 'good';
  return 'too-hot';
}

// Is `?debug=1` present? Gates the verbose telemetry (Fix D).
function isDebugEnabled() {
  try {
    return new URLSearchParams(window.location.search).get('debug') === '1';
  } catch {
    return false;
  }
}

/**
 * useStudioRecorder
 *
 * @param {object}   [options]
 * @param {number}   [options.countdownSeconds=COUNTDOWN_SECONDS]
 * @param {number}   [options.silenceRmsDbfs=SILENCE_RMS_DBFS] — windowed-max-RMS
 *        floor for the silence verdict (see SILENCE_RMS_DBFS).
 * @param {string}   [options.existingUrl]   — pre-existing recording to preview
 * @param {function} [options.onCapture]     — fires once a take exists, with
 *        `{ blob, duration, silenceResult }`. The consumer decides whether to
 *        forward the take downstream (we DO NOT forward silent takes here — the
 *        consumer reads `silenceResult.isSilent` and hard-blocks).
 *
 * @returns {object} flat recorder state + imperative handles (see bottom).
 */
export default function useStudioRecorder({
  countdownSeconds = COUNTDOWN_SECONDS,
  silenceRmsDbfs = SILENCE_RMS_DBFS,
  existingUrl = null,
  onCapture,
} = {}) {

  // ── Recorder lifecycle state ──────────────────────────────────────────────
  const [phase, setPhase] = useState('idle'); // idle | countdown | recording
  const [countdown, setCountdown] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(existingUrl || null);

  // Result of the silence measurement for the most recent take (Fix C).
  // `null` until a take exists. `{ isSilent, maxRmsDbfs, peakDbfs }` afterwards
  // (verdict is keyed on maxRmsDbfs; peakDbfs is telemetry-only).
  const [silenceResult, setSilenceResult] = useState(null);


  // ── Device + permission state (Fix B + permission model) ──────────────────
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceIdState] = useState(() => {
    try {
      return window.localStorage.getItem(MIC_DEVICE_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [permissionState, setPermissionState] = useState('unknown'); // granted|prompt|denied|unknown

  // ── Live meter state (Fix F) ──────────────────────────────────────────────
  const [level, setLevel] = useState(0); // normalized 0..1 for the VU bar
  const [band, setBand] = useState('silent'); // silent|quiet|good|too-hot

  // ── Refs (no re-render churn) ─────────────────────────────────────────────
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const startTimeRef = useRef(null);

  // Web Audio graph for the meter + silence measurement.
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  // Running peak (linear amplitude 0..1) accumulated DURING the recording
  // phase only. Kept for telemetry/comparison — NOT the verdict signal.
  const peakRef = useRef(0);
  // Running MAX of the per-window (short-term) RMS, accumulated DURING the
  // recording phase only. This is the load-bearing silence signal (Fix C):
  // "did the loudest ~46ms window ever reach speech level?".
  const maxRmsRef = useRef(0);
  // True while we should be accumulating (i.e. mediaRecorder is running).
  const measuringRef = useRef(false);


  const debug = isDebugEnabled();
  const log = useCallback((...args) => {
    if (debug) {
      // eslint-disable-next-line no-console
      console.log('[StudioRecorder]', ...args);
    }
  }, [debug]);

  // ── Permission query ──────────────────────────────────────────────────────
  // navigator.permissions is not universal (Safari historically lacked the
  // 'microphone' name), so this is best-effort.
  const queryPermission = useCallback(async () => {
    try {
      if (!navigator.permissions || !navigator.permissions.query) return;
      const status = await navigator.permissions.query({ name: 'microphone' });
      setPermissionState(status.state); // 'granted' | 'denied' | 'prompt'
      // Live-update if the user changes it in browser settings mid-session.
      status.onchange = () => setPermissionState(status.state);
    } catch {
      // Unsupported — leave as 'unknown'. The UI degrades gracefully.
    }
  }, []);

  // ── Device enumeration ────────────────────────────────────────────────────
  // Labels are only human-readable AFTER the first permission grant, so we
  // re-enumerate post-getUserMedia as well as on mount.
  const refreshDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const all = await navigator.mediaDevices.enumerateDevices();
      const inputs = all
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${i + 1}`,
        }));
      setDevices(inputs);
      log('enumerated audioinput devices:', inputs.map((d) => d.label));
    } catch (err) {
      log('enumerateDevices failed:', err?.message);
    }
  }, [log]);

  // Persisted setter for the chosen device (Fix B).
  const setSelectedDeviceId = useCallback((deviceId) => {
    setSelectedDeviceIdState(deviceId || '');
    try {
      if (deviceId) {
        window.localStorage.setItem(MIC_DEVICE_STORAGE_KEY, deviceId);
      } else {
        window.localStorage.removeItem(MIC_DEVICE_STORAGE_KEY);
      }
    } catch {
      // Private mode / storage disabled — selection still works for the session.
    }
  }, []);

  // Mount: query permission + enumerate. Also subscribe to device changes
  // (mic plugged/unplugged) so the picker stays fresh.
  useEffect(() => {
    queryPermission();
    refreshDevices();
    const onDeviceChange = () => refreshDevices();
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    }
    return () => {
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
      }
    };
  }, [queryPermission, refreshDevices]);

  // ── Audio-graph teardown helper ───────────────────────────────────────────
  const teardownAudioGraph = useCallback(() => {
    measuringRef.current = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch {
        /* already closed */
      }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  // ── Live meter RAF loop (Fix F) + peak accumulation (Fix C) ───────────────
  const startMeterLoop = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Float32Array(analyser.fftSize);

    const tick = () => {
      const a = analyserRef.current;
      if (!a) return;
      a.getFloatTimeDomainData(buf);

      let peak = 0;
      let sumSq = 0;
      for (let i = 0; i < buf.length; i += 1) {
        const v = Math.abs(buf[i]);
        if (v > peak) peak = v;
        sumSq += buf[i] * buf[i];
      }
      const rms = Math.sqrt(sumSq / buf.length);

      // Accumulate, ONLY while recording (Fix C): the loudest sample peak
      // (telemetry) AND the loudest short-term RMS window (the verdict signal —
      // "did any ~46ms window reach speech level?").
      if (measuringRef.current) {
        if (peak > peakRef.current) peakRef.current = peak;
        if (rms > maxRmsRef.current) maxRmsRef.current = rms;
      }


      // Live meter (Fix F). The bar tracks instantaneous RMS (perceptually
      // smoother than peak); the band uses instantaneous peak dBFS.
      setLevel(Math.min(1, rms * 3));
      setBand(dbfsToBand(amplitudeToDbfs(peak)));

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // ── Reset (used by "Re-record" / "Discard") ───────────────────────────────
  const reset = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(countdownRef.current);
    teardownAudioGraph();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    peakRef.current = 0;
    maxRmsRef.current = 0;
    setPhase('idle');

    setCountdown(0);
    setDuration(0);
    setAudioBlob(null);
    setSilenceResult(null);
    setLevel(0);
    setBand('silent');
    setAudioUrl((prev) => {
      // Revoke any object URL we created (don't revoke an existingUrl prop).
      if (prev && prev !== existingUrl && prev.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* noop */
        }
      }
      return null;
    });
  }, [teardownAudioGraph, existingUrl]);

  // ── Start ──────────────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      // Fix A — the load-bearing constraints. Explicitly disable all of
      // Chrome's implicit voice-DSP and pin the chosen device.
      const constraints = {
        audio: {
          echoCancellation: false,
          autoGainControl: false,
          noiseSuppression: false,
          ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {}),
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Permission just (re)granted — labels are now readable, so refresh the
      // picker and the permission badge.
      queryPermission();
      refreshDevices();

      // Telemetry (Fix D).
      const track = stream.getAudioTracks()[0];
      const settings = track?.getSettings ? track.getSettings() : {};
      log('stream acquired', {
        requestedDeviceId: selectedDeviceId || '(system default)',
        trackLabel: track?.label,
        sampleRate: settings.sampleRate,
        channelCount: settings.channelCount,
        echoCancellation: settings.echoCancellation,
        autoGainControl: settings.autoGainControl,
        noiseSuppression: settings.noiseSuppression,
      });

      // Build the Web Audio graph for the live meter + silence measurement.
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;
      peakRef.current = 0;
      maxRmsRef.current = 0;
      measuringRef.current = false; // not yet — only during the recording phase

      startMeterLoop();

      // MediaRecorder — created now, started after the countdown so the
      // hardware is fully warm.
      const mediaRecorder = new MediaRecorder(stream, { mimeType: MIME_TYPE });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          log('chunk', event.data.size, 'bytes');
        }
      };

      mediaRecorder.onstop = () => {
        measuringRef.current = false;
        const blob = new Blob(audioChunksRef.current, { type: MIME_TYPE });
        const url = URL.createObjectURL(blob);

        // Fix C — finalize the silence verdict from the loudest short-term RMS
        // window (peak kept for telemetry/comparison only).
        const peakDbfs = amplitudeToDbfs(peakRef.current);
        const maxRmsDbfs = amplitudeToDbfs(maxRmsRef.current);
        const isSilent =
          !Number.isFinite(maxRmsDbfs) || maxRmsDbfs < silenceRmsDbfs;
        const result = { isSilent, maxRmsDbfs, peakDbfs };

        const finalDuration = startTimeRef.current
          ? (Date.now() - startTimeRef.current) / 1000
          : 0;

        log('onstop', {
          blobSize: blob.size,
          chunkCount: audioChunksRef.current.length,
          maxRmsDbfs: Number.isFinite(maxRmsDbfs) ? maxRmsDbfs.toFixed(1) : '-Inf',
          peakDbfs: Number.isFinite(peakDbfs) ? peakDbfs.toFixed(1) : '-Inf',
          silenceRmsFloorDbfs: silenceRmsDbfs,
          isSilent,
          durationSecs: finalDuration.toFixed(2),
        });


        // Release the mic + audio graph now the take is captured.
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        teardownAudioGraph();
        setLevel(0);
        setBand('silent');

        setAudioBlob(blob);
        setAudioUrl(url);
        setSilenceResult(result);

        // Hand the take to the consumer. We pass the silence verdict along so
        // the consumer can hard-block (NOT upload / score) a silent take while
        // still letting the VA re-listen to it.
        if (onCapture) {
          try {
            onCapture({ blob, duration: finalDuration, silenceResult: result });
          } catch (cbErr) {
            // eslint-disable-next-line no-console
            console.error('[StudioRecorder] onCapture threw:', cbErr);
          }
        }
      };

      // Begin the countdown phase.
      setAudioBlob(null);
      setSilenceResult(null);
      setPhase('countdown');
      let remaining = countdownSeconds;
      setCountdown(remaining);

      countdownRef.current = setInterval(() => {
        remaining -= 1;
        if (remaining > 0) {
          setCountdown(remaining);
        } else {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setCountdown(0);

          // Start capturing. Begin peak/RMS accumulation NOW (Fix C) so the
          // countdown's ambient noise doesn't pollute the silence verdict.
          peakRef.current = 0;
          maxRmsRef.current = 0;
          measuringRef.current = true;

          mediaRecorder.start(100); // 100ms timeslice for reliable capture
          startTimeRef.current = Date.now();
          setPhase('recording');

          timerRef.current = setInterval(() => {
            setDuration(((Date.now() - startTimeRef.current) / 1000).toFixed(1));
          }, 100);
        }
      }, 1000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[StudioRecorder] getUserMedia error:', err);
      teardownAudioGraph();
      setPhase('idle');
      // Surface the most actionable failure mode to the permission badge.
      if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
        setPermissionState('denied');
      } else {
        queryPermission();
      }
    }
  }, [
    selectedDeviceId,
    countdownSeconds,
    silenceRmsDbfs,
    onCapture,

    log,
    queryPermission,
    refreshDevices,
    startMeterLoop,
    teardownAudioGraph,
  ]);

  // ── Cancel (during countdown) ─────────────────────────────────────────────
  const cancelCountdown = useCallback(() => {
    clearInterval(countdownRef.current);
    countdownRef.current = null;
    teardownAudioGraph();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    setPhase('idle');
    setCountdown(0);
    setLevel(0);
    setBand('silent');
  }, [teardownAudioGraph]);

  // ── Stop (end the take) ───────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && phase === 'recording') {
      mediaRecorderRef.current.stop();
      setPhase('idle');
      clearInterval(timerRef.current);
      setDuration(((Date.now() - startTimeRef.current) / 1000).toFixed(2));
    }
  }, [phase]);

  // ── Unmount cleanup ───────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
      teardownAudioGraph();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [teardownAudioGraph]);

  return {
    // lifecycle
    phase,
    countdown,
    duration,
    audioBlob,
    audioUrl,
    silenceResult,
    // devices + permission
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    refreshDevices,
    permissionState,
    // live meter
    level,
    band,
    // imperative handles
    startRecording,
    stopRecording,
    cancelCountdown,
    reset,
  };
}
